import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { branchesApi } from '@/features/branches/api/branches.api'
import type { Branch, CreateBranchPayload, UpdateBranchPayload } from '@/features/branches/domain/branch.types'
import { getApiErrorMessage, isCanceledError } from '@/shared/api/api-error'

// `sort` is documented but never serialized back, so it can only ever tie-break within one page load.
const sortBranches = (first: Branch, second: Branch) =>
  (first.sort ?? 0) - (second.sort ?? 0) || first.name.localeCompare(second.name)

const normalizeName = (name: string) => name.trim().toLocaleLowerCase()

export const useBranchesStore = defineStore('branches', () => {
  const branches = ref<Branch[]>([])
  const loadingList = ref(false)
  const saving = ref(false)
  const deletingIds = ref<Set<number>>(new Set())
  const error = ref<string | null>(null)

  const sortedBranches = computed(() => [...branches.value].sort(sortBranches))
  const hasBranches = computed(() => branches.value.length > 0)

  const findById = (id: number | string | null | undefined) =>
    id === null || id === undefined ? undefined : branches.value.find((branch) => branch.id === Number(id))
  const isDeleting = (id: number | string) => deletingIds.value.has(Number(id))

  /** Mirrors the backend's case-insensitive uniqueness so the 409 is prevented, not just reported. */
  const isNameTaken = (name: string, exceptId?: number | null) =>
    branches.value.some((branch) => branch.id !== exceptId && normalizeName(branch.name) === normalizeName(name))

  const resetError = () => {
    error.value = null
  }

  const setDeleting = (id: number | string, value: boolean) => {
    const nextDeletingIds = new Set(deletingIds.value)

    if (value) {
      nextDeletingIds.add(Number(id))
    } else {
      nextDeletingIds.delete(Number(id))
    }

    deletingIds.value = nextDeletingIds
  }

  const upsertBranch = (payload: Branch) => {
    const index = branches.value.findIndex((branch) => branch.id === payload.id)

    if (index >= 0) {
      branches.value[index] = payload
    } else {
      branches.value.push(payload)
    }
  }

  const fetchBranches = async (signal?: AbortSignal) => {
    loadingList.value = true
    resetError()

    try {
      branches.value = await branchesApi.list(signal)
      return branches.value
    } catch (requestError) {
      if (!isCanceledError(requestError)) error.value = getApiErrorMessage(requestError)
      throw requestError
    } finally {
      loadingList.value = false
    }
  }

  const createBranch = async (payload: CreateBranchPayload) => {
    saving.value = true
    resetError()

    try {
      const branch = await branchesApi.create(payload)
      upsertBranch(branch)
      return branch
    } catch (requestError) {
      if (!isCanceledError(requestError)) error.value = getApiErrorMessage(requestError)
      throw requestError
    } finally {
      saving.value = false
    }
  }

  const updateBranch = async (id: number | string, payload: UpdateBranchPayload) => {
    saving.value = true
    resetError()

    try {
      const branch = await branchesApi.update(id, payload)
      upsertBranch(branch)
      return branch
    } catch (requestError) {
      if (!isCanceledError(requestError)) error.value = getApiErrorMessage(requestError)
      throw requestError
    } finally {
      saving.value = false
    }
  }

  const deleteBranch = async (id: number | string) => {
    setDeleting(id, true)
    resetError()

    try {
      await branchesApi.delete(id)
      branches.value = branches.value.filter((branch) => branch.id !== Number(id))
    } catch (requestError) {
      if (!isCanceledError(requestError)) error.value = getApiErrorMessage(requestError)
      throw requestError
    } finally {
      setDeleting(id, false)
    }
  }

  return {
    branches,
    loadingList,
    saving,
    deletingIds,
    error,
    sortedBranches,
    hasBranches,
    findById,
    isDeleting,
    isNameTaken,
    resetError,
    fetchBranches,
    createBranch,
    updateBranch,
    deleteBranch,
  }
})
